/** Trello-style checklists embedded in an issue detail page. */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, GripVertical, ListChecks, Pencil, Plus, Trash2, X } from "lucide-react";
import type { ChecklistDto, ChecklistItemDto } from "../../shared/contracts/issues";
import { api, formatInstant } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function ChecklistSection({ issueRef, onMutation }: { issueRef: string; onMutation?: () => void }) {
  const [checklists, setChecklists] = useState<ChecklistDto[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [addingChecklist, setAddingChecklist] = useState(false);
  const [checklistDraft, setChecklistDraft] = useState("");
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [itemDraft, setItemDraft] = useState("");
  const [editingChecklist, setEditingChecklist] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [hideCompleted, setHideCompleted] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const loadRequest = useRef(0);
  const checklistSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const load = useCallback(() => {
    const request = ++loadRequest.current;
    setLoadError(null);
    setChecklists(null);
    api.checklists(issueRef).then((result) => {
      if (loadRequest.current === request) setChecklists(result);
    }).catch((error: unknown) => {
      if (loadRequest.current === request) setLoadError(error);
    });
  }, [issueRef]);

  useEffect(load, [load]);

  const run = async (key: string, operation: () => Promise<void>) => {
    setActionError(null);
    setBusy(key);
    try {
      await operation();
      onMutation?.();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Checklist action failed");
    } finally {
      setBusy(null);
    }
  };

  const replaceChecklist = (updated: ChecklistDto) => {
    setChecklists((current) => current?.map((checklist) => (checklist.id === updated.id ? updated : checklist)) ?? null);
  };

  const replaceItem = (updated: ChecklistItemDto) => {
    setChecklists((current) =>
      current?.map((checklist) =>
        checklist.id === updated.checklist_id
          ? { ...checklist, items: checklist.items.map((item) => (item.id === updated.id ? updated : item)) }
          : checklist,
      ) ?? null,
    );
  };

  const reorderChecklist = (activeId: string, overId: string) => {
    if (!checklists || busy !== null) return;
    const from = checklists.findIndex((checklist) => checklist.id === activeId);
    const to = checklists.findIndex((checklist) => checklist.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const previous = checklists;
    const reordered = [...previous];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved!);
    const optimistic = reordered.map((checklist, position) => ({ ...checklist, position }));
    setChecklists(optimistic);
    void run("reorder-checklists", async () => {
      try {
        setChecklists(await api.reorderChecklists(issueRef, optimistic.map((checklist) => checklist.id)));
      } catch (error) {
        setChecklists(previous);
        throw error;
      }
    });
  };

  const onChecklistDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.data.current?.checklistId;
    const overId = event.over?.data.current?.checklistId;
    if (typeof activeId === "string" && typeof overId === "string") reorderChecklist(activeId, overId);
  };

  const createChecklist = (event: React.FormEvent) => {
    event.preventDefault();
    if (!checklistDraft.trim()) return;
    void run("create-checklist", async () => {
      const created = await api.createChecklist(issueRef, checklistDraft);
      setChecklists((current) => [...(current ?? []), created]);
      setChecklistDraft("");
      setAddingChecklist(false);
      setAddingTo(created.id);
      setItemDraft("");
    });
  };

  return (
    <section className="checklists overflow-hidden rounded-lg border border-border bg-card" aria-labelledby="checklists-heading">
      <header className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-3">
        <ListChecks className="size-4 text-muted-foreground" aria-hidden="true" />
        <h2 id="checklists-heading" className="text-sm font-semibold">Checklists</h2>
        {checklists && checklists.length > 0 && (
          <span className="rounded-full border border-border px-1.5 text-[11px] text-muted-foreground">{checklists.length}</span>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => setAddingChecklist(true)}
          disabled={addingChecklist || busy !== null}
        >
          <Plus aria-hidden="true" />
          Add checklist
        </Button>
      </header>

      <div className="p-4">
        {loadError ? (
          <div className="flex items-center gap-3 text-sm text-destructive">
            <span>Could not load checklists.</span>
            <Button type="button" variant="outline" size="sm" onClick={load}>Retry</Button>
          </div>
        ) : checklists === null ? (
          <p className="text-sm text-muted-foreground">Loading checklists…</p>
        ) : (
          <div className="flex flex-col gap-5">
            {checklists.length === 0 && !addingChecklist && (
              <p className="text-sm text-muted-foreground">Break this issue into small, trackable steps.</p>
            )}

            <DndContext sensors={checklistSensors} collisionDetection={closestCenter} onDragEnd={onChecklistDragEnd}>
            {checklists.map((checklist) => (
              <ChecklistCard
                key={checklist.id}
                checklist={checklist}
                busy={busy}
                hideCompleted={hideCompleted[checklist.id] ?? false}
                collapsed={collapsed[checklist.id] ?? false}
                editingChecklist={editingChecklist}
                editingItem={editingItem}
                editDraft={editDraft}
                addingTo={addingTo}
                itemDraft={itemDraft}
                onHideCompleted={(hidden) => setHideCompleted((current) => ({ ...current, [checklist.id]: hidden }))}
                onCollapsed={(value) => setCollapsed((current) => ({ ...current, [checklist.id]: value }))}
                onEditDraft={setEditDraft}
                onStartChecklistEdit={() => {
                  setEditingItem(null);
                  setEditingChecklist(checklist.id);
                  setEditDraft(checklist.title);
                }}
                onCancelChecklistEdit={() => setEditingChecklist(null)}
                onSaveChecklist={(event) => {
                  event.preventDefault();
                  if (!editDraft.trim()) return;
                  void run(`checklist-${checklist.id}`, async () => {
                    replaceChecklist(await api.updateChecklist(checklist.id, editDraft));
                    setEditingChecklist(null);
                  });
                }}
                onDeleteChecklist={() => {
                  if (!confirm(`Delete checklist “${checklist.title}” and all of its items?`)) return;
                  void run(`checklist-${checklist.id}`, async () => {
                    await api.deleteChecklist(checklist.id);
                    setChecklists((current) => current?.filter((candidate) => candidate.id !== checklist.id) ?? null);
                  });
                }}
                onToggleItem={(item) => {
                  const completed = !item.completed;
                  replaceItem({
                    ...item,
                    completed,
                    completed_at: completed ? new Date().toISOString() : null,
                  });
                  void run(`item-${item.id}`, async () => {
                    try {
                      replaceItem(await api.updateChecklistItem(item.id, { completed }));
                    } catch (error) {
                      replaceItem(item);
                      throw error;
                    }
                  });
                }}
                onStartItemEdit={(item) => {
                  setEditingChecklist(null);
                  setEditingItem(item.id);
                  setEditDraft(item.title);
                }}
                onCancelItemEdit={() => setEditingItem(null)}
                onSaveItem={(event, item) => {
                  event.preventDefault();
                  if (!editDraft.trim()) return;
                  void run(`item-${item.id}`, async () => {
                    replaceItem(await api.updateChecklistItem(item.id, { title: editDraft }));
                    setEditingItem(null);
                  });
                }}
                onDeleteItem={(item) => {
                  void run(`item-${item.id}`, async () => {
                    await api.deleteChecklistItem(item.id);
                    setChecklists((current) =>
                      current?.map((candidate) =>
                        candidate.id === checklist.id
                          ? { ...candidate, items: candidate.items.filter((value) => value.id !== item.id) }
                          : candidate,
                      ) ?? null,
                    );
                  });
                }}
                onReorderItems={(activeId, overId) => {
                  const from = checklist.items.findIndex((item) => item.id === activeId);
                  const to = checklist.items.findIndex((item) => item.id === overId);
                  if (from < 0 || to < 0 || from === to || busy !== null) return;
                  const previous = checklist.items;
                  const reordered = [...previous];
                  const [moved] = reordered.splice(from, 1);
                  reordered.splice(to, 0, moved!);
                  const optimistic = reordered.map((item, position) => ({ ...item, position }));
                  setChecklists((current) =>
                    current?.map((candidate) => candidate.id === checklist.id ? { ...candidate, items: optimistic } : candidate) ?? null,
                  );
                  void run(`reorder-${checklist.id}`, async () => {
                    try {
                      const persisted = await api.reorderChecklistItems(checklist.id, optimistic.map((item) => item.id));
                      setChecklists((current) =>
                        current?.map((candidate) => candidate.id === checklist.id ? { ...candidate, items: persisted } : candidate) ?? null,
                      );
                    } catch (error) {
                      setChecklists((current) =>
                        current?.map((candidate) => candidate.id === checklist.id ? { ...candidate, items: previous } : candidate) ?? null,
                      );
                      throw error;
                    }
                  });
                }}
                onStartAdd={() => {
                  setAddingTo(checklist.id);
                  setItemDraft("");
                }}
                onItemDraft={setItemDraft}
                onCancelAdd={() => {
                  setAddingTo(null);
                  setItemDraft("");
                }}
                onAddItem={(event) => {
                  event.preventDefault();
                  const title = itemDraft.trim();
                  if (!title || busy !== null) return;
                  // Keep the composer open and clear it immediately so users
                  // can type the next step without repeatedly reopening it.
                  setItemDraft("");
                  void run(`add-${checklist.id}`, async () => {
                    try {
                      const created = await api.createChecklistItem(checklist.id, title);
                      setChecklists((current) =>
                        current?.map((candidate) =>
                          candidate.id === checklist.id ? { ...candidate, items: [...candidate.items, created] } : candidate,
                        ) ?? null,
                      );
                    } catch (error) {
                      setItemDraft((current) => current || title);
                      throw error;
                    }
                  });
                }}
              />
            ))}
            </DndContext>

            {addingChecklist && (
              <form className="checklist-create flex max-w-xl gap-2" onSubmit={createChecklist}>
                <Input
                  autoFocus
                  value={checklistDraft}
                  onChange={(event) => setChecklistDraft(event.target.value)}
                  placeholder="Checklist title"
                  aria-label="Checklist title"
                  maxLength={200}
                  disabled={busy !== null}
                />
                <Button type="submit" size="sm" disabled={!checklistDraft.trim() || busy !== null}>Add</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => {
                  setAddingChecklist(false);
                  setChecklistDraft("");
                }}>Cancel</Button>
              </form>
            )}

            {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

interface ChecklistCardProps {
  checklist: ChecklistDto;
  busy: string | null;
  hideCompleted: boolean;
  collapsed: boolean;
  editingChecklist: string | null;
  editingItem: string | null;
  editDraft: string;
  addingTo: string | null;
  itemDraft: string;
  onHideCompleted: (hidden: boolean) => void;
  onCollapsed: (collapsed: boolean) => void;
  onEditDraft: (value: string) => void;
  onStartChecklistEdit: () => void;
  onCancelChecklistEdit: () => void;
  onSaveChecklist: (event: React.FormEvent) => void;
  onDeleteChecklist: () => void;
  onToggleItem: (item: ChecklistItemDto) => void;
  onStartItemEdit: (item: ChecklistItemDto) => void;
  onCancelItemEdit: () => void;
  onSaveItem: (event: React.FormEvent, item: ChecklistItemDto) => void;
  onDeleteItem: (item: ChecklistItemDto) => void;
  onReorderItems: (activeId: string, overId: string) => void;
  onStartAdd: () => void;
  onItemDraft: (value: string) => void;
  onCancelAdd: () => void;
  onAddItem: (event: React.FormEvent) => void;
}

function ChecklistCard(props: ChecklistCardProps) {
  const { checklist } = props;
  const itemInput = useRef<HTMLInputElement>(null);
  const completed = checklist.items.filter((item) => item.completed).length;
  const percent = checklist.items.length === 0 ? 0 : Math.round((completed / checklist.items.length) * 100);
  const visibleItems = props.hideCompleted ? checklist.items.filter((item) => !item.completed) : checklist.items;
  const disabled = props.busy !== null;
  const checklistDraggable = useDraggable({
    id: `checklist:${checklist.id}`,
    data: { checklistId: checklist.id },
    disabled,
  });
  const checklistDroppable = useDroppable({
    id: `checklist:${checklist.id}`,
    data: { checklistId: checklist.id },
    disabled,
  });
  const setChecklistNodeRef = (node: HTMLElement | null) => {
    checklistDraggable.setNodeRef(node);
    checklistDroppable.setNodeRef(node);
  };
  const checklistTransform = checklistDraggable.transform;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id;
    if (overId !== undefined) props.onReorderItems(String(event.active.id), String(overId));
  };

  useEffect(() => {
    if (props.addingTo === checklist.id && !disabled && !props.collapsed) itemInput.current?.focus();
  }, [checklist.id, disabled, props.addingTo, props.collapsed]);

  return (
    <article
      ref={setChecklistNodeRef}
      className={`checklist rounded-md ${
        checklistDroppable.isOver && !checklistDraggable.isDragging ? "bg-accent/60" : ""
      } ${checklistDraggable.isDragging ? "relative z-20 bg-card opacity-75 shadow-md" : ""}`}
      style={checklistTransform ? {
        transform: `translate3d(${checklistTransform.x}px, ${checklistTransform.y}px, 0)`,
      } : undefined}
      aria-labelledby={`checklist-${checklist.id}`}
    >
      <div className="flex items-start gap-2">
        <Button
          ref={checklistDraggable.setActivatorNodeRef}
          type="button"
          variant="ghost"
          size="icon-sm"
          className="-ml-1 flex-none cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          {...checklistDraggable.attributes}
          {...checklistDraggable.listeners}
          aria-label={`Reorder checklist ${checklist.title}`}
          title="Drag to reorder this checklist"
          disabled={disabled}
        >
          <GripVertical aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="flex-none text-muted-foreground"
          aria-label={`${props.collapsed ? "Expand" : "Collapse"} checklist ${checklist.title}`}
          aria-expanded={!props.collapsed}
          onClick={() => props.onCollapsed(!props.collapsed)}
        >
          {props.collapsed ? <ChevronRight aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
        </Button>
        {props.editingChecklist === checklist.id ? (
          <form className="flex min-w-0 flex-1 gap-2" onSubmit={props.onSaveChecklist}>
            <Input
              autoFocus
              value={props.editDraft}
              onChange={(event) => props.onEditDraft(event.target.value)}
              aria-label="Edit checklist title"
              maxLength={200}
              disabled={disabled}
            />
            <Button type="submit" size="icon-sm" aria-label="Save checklist title" disabled={!props.editDraft.trim() || disabled}>
              <Check aria-hidden="true" />
            </Button>
            <Button type="button" variant="ghost" size="icon-sm" aria-label="Cancel checklist title edit" onClick={props.onCancelChecklistEdit}>
              <X aria-hidden="true" />
            </Button>
          </form>
        ) : (
          <>
            <h3 id={`checklist-${checklist.id}`} className="min-w-0 flex-1 break-words pt-1 font-semibold">{checklist.title}</h3>
            {props.collapsed && (
              <span className="pt-1 text-xs tabular-nums text-muted-foreground">{completed}/{checklist.items.length}</span>
            )}
            <Button type="button" variant="ghost" size="icon-sm" aria-label={`Edit checklist ${checklist.title}`} onClick={props.onStartChecklistEdit} disabled={disabled}>
              <Pencil aria-hidden="true" />
            </Button>
            <Button type="button" variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive" aria-label={`Delete checklist ${checklist.title}`} onClick={props.onDeleteChecklist} disabled={disabled}>
              <Trash2 aria-hidden="true" />
            </Button>
          </>
        )}
      </div>

      {!props.collapsed && <>
      <div className="mt-3 flex items-center gap-3 pl-7">
        <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">{percent}%</span>
        <div
          className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-label={`${checklist.title} progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className={`h-full rounded-full transition-[width] ${percent === 100 ? "bg-success" : "bg-primary"}`} style={{ width: `${percent}%` }} />
        </div>
        {completed > 0 && (
          <Button type="button" variant="ghost" size="xs" onClick={() => props.onHideCompleted(!props.hideCompleted)}>
            {props.hideCompleted ? `Show completed (${completed})` : "Hide completed"}
          </Button>
        )}
      </div>

      <div className="mt-2 pl-7">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <ul className="flex flex-col gap-0.5">
            {visibleItems.map((item, visibleIndex) => {
              return (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  disabled={disabled}
                  editing={props.editingItem === item.id}
                  editDraft={props.editDraft}
                  canMoveUp={visibleIndex > 0}
                  canMoveDown={visibleIndex < visibleItems.length - 1}
                  onToggle={() => props.onToggleItem(item)}
                  onEditDraft={props.onEditDraft}
                  onStartEdit={() => props.onStartItemEdit(item)}
                  onCancelEdit={props.onCancelItemEdit}
                  onSave={(event) => props.onSaveItem(event, item)}
                  onDelete={() => props.onDeleteItem(item)}
                  onMoveUp={() => props.onReorderItems(item.id, visibleItems[visibleIndex - 1]!.id)}
                  onMoveDown={() => props.onReorderItems(item.id, visibleItems[visibleIndex + 1]!.id)}
                />
              );
            })}
          </ul>
        </DndContext>

        {props.addingTo === checklist.id ? (
          <div className="mt-2 max-w-xl">
            <form className="checklist-item-create flex gap-2" onSubmit={props.onAddItem}>
              <Input
                ref={itemInput}
                autoFocus
                value={props.itemDraft}
                onChange={(event) => props.onItemDraft(event.target.value)}
                placeholder="Add an item"
                aria-label={`Add item to ${checklist.title}`}
                aria-describedby={`checklist-item-hint-${checklist.id}`}
                maxLength={500}
                disabled={disabled}
              />
              <Button type="submit" size="sm" disabled={!props.itemDraft.trim() || disabled}>Add</Button>
              <Button type="button" size="sm" variant="ghost" onClick={props.onCancelAdd} disabled={disabled}>Done</Button>
            </form>
            <p id={`checklist-item-hint-${checklist.id}`} className="mt-1.5 text-xs text-muted-foreground">
              Press Enter to add and keep going.
            </p>
          </div>
        ) : (
          <Button type="button" variant="ghost" size="sm" className="mt-1 text-muted-foreground" onClick={props.onStartAdd} disabled={disabled}>
            <Plus aria-hidden="true" />
            Add an item
          </Button>
        )}
      </div>
      </>}
    </article>
  );
}

interface ChecklistItemRowProps {
  item: ChecklistItemDto;
  disabled: boolean;
  editing: boolean;
  editDraft: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: () => void;
  onEditDraft: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (event: React.FormEvent) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function ChecklistItemRow(props: ChecklistItemRowProps) {
  const { item } = props;
  const draggable = useDraggable({ id: item.id, disabled: props.disabled });
  const droppable = useDroppable({ id: item.id, disabled: props.disabled });
  const setNodeRef = (node: HTMLLIElement | null) => {
    draggable.setNodeRef(node);
    droppable.setNodeRef(node);
  };
  const transform = draggable.transform;

  return (
    <li
      ref={setNodeRef}
      className={`checklist-item group flex min-h-9 items-center gap-1 rounded-md px-1 hover:bg-muted/60 ${
        droppable.isOver && !draggable.isDragging ? "bg-accent" : ""
      } ${draggable.isDragging ? "relative z-10 opacity-70 shadow-sm" : ""}`}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
    >
      <Button
        ref={draggable.setActivatorNodeRef}
        type="button"
        variant="ghost"
        size="icon-xs"
        className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        {...draggable.attributes}
        {...draggable.listeners}
        aria-label={`Reorder item ${item.title}`}
        title="Drag to reorder; keyboard users can press Space, then use arrow keys"
        disabled={props.disabled}
      >
        <GripVertical aria-hidden="true" />
      </Button>
      <input
        type="checkbox"
        className="size-4 flex-none accent-primary"
        checked={item.completed}
        onChange={props.onToggle}
        disabled={props.disabled}
        aria-label={`${item.completed ? "Mark incomplete" : "Mark complete"}: ${item.title}`}
      />
      {props.editing ? (
        <form className="flex min-w-0 flex-1 gap-2 py-1" onSubmit={props.onSave}>
          <Input
            autoFocus
            value={props.editDraft}
            onChange={(event) => props.onEditDraft(event.target.value)}
            aria-label="Edit checklist item"
            maxLength={500}
            disabled={props.disabled}
          />
          <Button type="submit" size="icon-sm" aria-label="Save checklist item" disabled={!props.editDraft.trim() || props.disabled}>
            <Check aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" aria-label="Cancel checklist item edit" onClick={props.onCancelEdit}>
            <X aria-hidden="true" />
          </Button>
        </form>
      ) : (
        <>
          <span className="min-w-0 flex-1 break-words px-1">
            <span className={`block text-sm ${item.completed ? "text-muted-foreground line-through" : ""}`}>
              {item.title}
            </span>
            {item.completed && item.completed_at && (
              <time className="block text-[11px] text-muted-foreground" dateTime={item.completed_at}>
                Completed {formatInstant(item.completed_at)}
              </time>
            )}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"
            aria-label={`Move item ${item.title} up`}
            onClick={props.onMoveUp}
            disabled={props.disabled || !props.canMoveUp}
          >
            <ArrowUp aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"
            aria-label={`Move item ${item.title} down`}
            onClick={props.onMoveDown}
            disabled={props.disabled || !props.canMoveDown}
          >
            <ArrowDown aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="icon-xs" className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100" aria-label={`Edit item ${item.title}`} onClick={props.onStartEdit} disabled={props.disabled}>
            <Pencil aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="icon-xs" className="text-muted-foreground opacity-0 hover:text-destructive group-focus-within:opacity-100 group-hover:opacity-100" aria-label={`Delete item ${item.title}`} onClick={props.onDelete} disabled={props.disabled}>
            <Trash2 aria-hidden="true" />
          </Button>
        </>
      )}
    </li>
  );
}
